import type { ReactNode } from 'react';
import { isPublicItem, variantOf, type Block, type BlockOf, type IconKey } from '@agency/contracts';
import { CircleArrow, Eyebrow, HeroObject, Highlighted, Icon3d, ItemIcon, LineIcon, Pill, SplitGradient, is3d, safeHref } from './ui.tsx';
import { blockMessages, caseStudyAction } from './messages.ts';

/**
 * Pure, server-safe presentational blocks shared by the public site and the editor canvas. No data fetching, no server code, no client state.
 * Look: halo-lab.com, owner-authorized (decision 0004). Proof (logos, metrics, testimonials, cases) renders only from verified/real data.
 */

function PipelineSchematic({ locale }: { locale?: string }) {
  const t = blockMessages(locale);
  const stages = [t.inquiry, t.qualified, t.proposal, t.won];
  return (
    <figure className="pb-schematic pb-light" aria-label={t.pipelineDiagram}>
      <div className="pb-schematic__cols" aria-hidden="true">
        {stages.map((s, i) => (
          <div key={s} className="pb-schematic__col">
            <span className="pb-schematic__stage">{s}</span>
            {Array.from({ length: [3, 2, 2, 1][i]! }).map((_, k) => (
              <span key={k} className={`pb-schematic__card${i === 3 ? ' is-won' : ''}`}>
                <i /><i />
              </span>
            ))}
          </div>
        ))}
      </div>
      <figcaption className="pb-schematic__cap">{t.pipelineCaption}</figcaption>
    </figure>
  );
}

export interface RenderContext {
  /** The inquiry form is injected so the public site can submit and the editor canvas can show an inert preview. */
  renderForm: (block: BlockOf<'form'>) => ReactNode;
  locale?: string;
  /** Brand details for contact blocks (from site settings). */
  brand?: { name?: string; email?: string; phone?: string; address?: string };
  /** Resolved data for connected blocks. The renderer never fetches. */
  services?: { key: string; name: string; description: string; href: string }[];
  articles?: { title: string; href: string; summary?: string; publishedAt?: string }[];
  caseStudies?: { slug: string; title: string; summary: string; href: string; industry?: string; coverImageUrl?: string }[];
  /** Editor canvas only: show unverified claims, flagged, instead of hiding them. */
  showDraftClaims?: boolean;
}

type Theme = NonNullable<Block['theme']>;
type Presentation = { id: string; theme?: Theme; spacing?: 'compact' | 'regular' | 'loose'; visibility?: 'all' | 'desktop' | 'mobile'; analyticsId?: string; inRun?: boolean };
const DARK: Theme[] = ['strong', 'night', 'night-fade'];

/** Shared section frame: heading id for aria-labelledby, theme/spacing/visibility, analytics id as a data attribute (read by the first-party collector). */
function Frame({ block, theme, children, className = '', narrow, labelled = true, wrap = true }: { block: Presentation; theme?: Theme; children: ReactNode; className?: string; narrow?: boolean; labelled?: boolean; wrap?: boolean }) {
  const t = block.inRun ? 'run' : (block.theme ?? theme ?? 'default');
  const dark = block.inRun || DARK.includes(t as Theme);
  return (
    <section
      className={`pb-section pb-t-${t} pb-s-${block.spacing ?? 'regular'} pb-v-${block.visibility ?? 'all'}${dark ? ' pb-dark' : ''} ${className}`.trim()}
      aria-labelledby={labelled ? `${block.id}-h` : undefined}
      data-analytics-id={block.analyticsId}
      data-block-id={block.id}
    >
      {wrap ? <div className={`pb-wrap${narrow ? ' pb-narrow' : ''}`}>{children}</div> : children}
    </section>
  );
}
const H2 = ({ id, children, size, center, className = '' }: { id: string; children: ReactNode; size?: 'xl' | 'md'; center?: boolean; className?: string }) => (
  <h2 id={`${id}-h`} className={`pb-h2${size ? ` pb-h2--${size}` : ''}${center ? ' pb-center' : ''} ${className}`.trim()}>{children}</h2>
);
const DraftTag = () => <span className="pb-draft" role="note">Draft claim, hidden publicly until verified</span>;
const visible = <T extends { verified?: boolean }>(items: T[], ctx: RenderContext) => items.filter((i) => ctx.showDraftClaims || isPublicItem(i));
const pick = <T,>(set: readonly T[], i: number) => set[i % set.length]!;

const TEAM_3D: IconKey[] = ['cube', 'bolt', 'asterisk'];
const ADV_3D: IconKey[] = ['person', 'compass', 'sliders', 'puzzle', 'flow', 'database'];
const BADGES: IconKey[] = ['code', 'layers', 'browser', 'plug', 'shield', 'chart', 'mobile', 'cloud', 'cog'];
/** Line icons on the light illustration panel (the glass renders are made for dark backgrounds). */
const PROBLEM_ICONS: IconKey[] = ['search', 'plug', 'layers', 'chart', 'shield'];
const COLUMN_BADGES: IconKey[] = ['pen', 'code', 'chart'];

// ---------------------------------------------------------------- hero
export function HeroBlock({ block, onPrimary, locale }: { block: BlockOf<'hero'> & { inRun?: boolean }; onPrimary?: ReactNode; locale?: string }) {
  const v = block.variant;
  const centered = v === 'centered-visual' || v === 'centered-dark';
  const split = !centered && v === 'editorial-split' && block.visual === 'pipeline';
  const primary = onPrimary ?? <Pill href="#inquiry" label={block.primaryCta.label} variant={v === 'centered-dark' ? 'warm' : 'primary'} icon={v === 'centered-dark' ? 'mail' : 'arrow-up-right'} />;
  const secondary = block.secondaryCta ? <Pill href={block.secondaryCta.href} label={block.secondaryCta.label} variant="outline-night" icon={null} /> : null;
  // The centered home hero shows the 3D object unless the editor chose "Typographic"; the pipeline diagram belongs to the split layout.
  const showObject = v === 'centered-visual' && block.visual !== 'none';
  return (
    <section
      className={`pb-hero pb-hero--${v} pb-dark pb-v-${block.visibility ?? 'all'}${block.inRun ? ' pb-in-run' : ' pb-hero--solid'}`}
      aria-labelledby={`${block.id}-h`}
      data-analytics-id={block.analyticsId}
      data-block-id={block.id}
    >
      {v === 'centered-visual' ? <div className="pb-stars" aria-hidden="true" /> : null}
      {v === 'centered-dark' ? <div className="pb-hero__glow" aria-hidden="true" /> : null}
      <div className={`pb-wrap ${centered ? 'pb-hero__center' : split ? 'pb-hero__grid' : 'pb-hero__text'}`}>
        {showObject ? <HeroObject /> : null}
        <div className="pb-hero__copy">
          {block.eyebrow ? <p className="pb-eyebrow pb-hero__eyebrow">{block.eyebrow}</p> : null}
          <h1 id={`${block.id}-h`} className={`pb-display${v === 'centered-dark' ? ' pb-display--service' : ''}`}><Highlighted text={block.headline} highlight={block.highlight} /></h1>
          {block.subheadline ? <p className="pb-lede pb-hero__lede">{block.subheadline}</p> : null}
          <div className="pb-actions">{primary}{secondary}</div>
          {block.proofPoints.length ? <ul className="pb-facts">{block.proofPoints.map((p) => <li key={p}>{p}</li>)}</ul> : null}
        </div>
        {split ? <div className="pb-hero__visual"><PipelineSchematic locale={locale} /></div> : null}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- explain
function IconGrid({ items, icons, numbered, locale }: { items: { title: string; body: string; icon?: IconKey; href?: string }[]; icons?: IconKey[]; numbered?: boolean; locale?: string }) {
  const t = blockMessages(locale);
  return (
    <ul className="pb-hgrid">
      {items.map((it, i) => (
        <li key={`${it.title}-${i}`} className="pb-hgrid__cell">
          {icons ? <ItemIcon name={it.icon ?? pick(icons, i)} size={is3d(it.icon ?? pick(icons, i)) ? 44 : 40} /> : numbered ? <span className="pb-num" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span> : null}
          <h3 className="pb-h3">{it.title}</h3>
          <p className="pb-body">{it.body}</p>
          {it.href ? <a className="pb-more" href={safeHref(it.href)}>{t.learnMore}<span className="pb-visually-hidden"> {t.about} {it.title}</span> <LineIcon name="arrow-up-right" /></a> : null}
        </li>
      ))}
    </ul>
  );
}

function SectionHead({ id, eyebrow, heading, intro, center, serif }: { id: string; eyebrow?: string; heading: string; intro?: string; center?: boolean; serif?: boolean }) {
  return (
    <div className={`pb-head${center ? ' pb-head--center' : ''}`}>
      {eyebrow ? <Eyebrow serif={serif}>{eyebrow}</Eyebrow> : null}
      <H2 id={id} center={center}>{heading}</H2>
      {intro ? <p className="pb-lede">{intro}</p> : null}
    </div>
  );
}

export function BenefitsBlock({ block, locale }: { block: BlockOf<'benefits'> & { inRun?: boolean }; locale?: string }) {
  const grid = variantOf('benefits', block.variant) === 'grid';
  return (
    <Frame block={block}>
      <SectionHead id={block.id} eyebrow={block.eyebrow} heading={block.heading} center={grid} serif />
      <IconGrid items={block.items} icons={grid ? ADV_3D : undefined} numbered={!grid} locale={locale} />
    </Frame>
  );
}

function FeaturesBlock({ block, locale }: { block: (BlockOf<'features'> | BlockOf<'useCases'>) & { inRun?: boolean }; locale?: string }) {
  const grid = variantOf(block.type, block.variant) === 'grid';
  return (
    <Frame block={block}>
      <SectionHead id={block.id} eyebrow={block.eyebrow} heading={block.heading} center={grid} serif />
      <IconGrid items={block.items} icons={BADGES} locale={locale} />
    </Frame>
  );
}

function ServicesBlock({ block, locale }: { block: BlockOf<'services'> & { inRun?: boolean }; locale?: string }) {
  if (variantOf('services', block.variant) === 'team-row') {
    return (
      <Frame block={block} className="pb-teamrow-section">
        <h2 id={`${block.id}-h`} className="pb-visually-hidden">{block.heading}</h2>
        <ul className="pb-teamrow">
          {block.items.map((it, i) => {
            const inner = (
              <>
                <Icon3d name={is3d(it.icon) ? it.icon : (pick(TEAM_3D, i) as 'cube')} size={64} />
                <span className="pb-teamrow__text"><span className="pb-teamrow__title">{it.title}</span><span className="pb-teamrow__sub">{it.body}</span></span>
                <LineIcon name="arrow-up-right" className="pb-teamrow__arrow" />
              </>
            );
            return <li key={`${it.title}-${i}`}>{it.href ? <a className="pb-teamrow__item" href={safeHref(it.href)}>{inner}</a> : <div className="pb-teamrow__item">{inner}</div>}</li>;
          })}
        </ul>
      </Frame>
    );
  }
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} intro={block.intro} />
      <IconGrid items={block.items} icons={BADGES} locale={locale} />
    </Frame>
  );
}

function ProblemBlock({ block }: { block: BlockOf<'problem'> & { inRun?: boolean } }) {
  if (variantOf('problem', block.variant) === 'cards') {
    return (
      <Frame block={block}>
        <div className="pb-head pb-head--center">
          <H2 id={block.id} center className="pb-h2--caps"><SplitGradient text={block.heading} tone="warm" /></H2>
          {block.intro ? <p className="pb-lede">{block.intro}</p> : null}
        </div>
        <ul className="pb-cards3">
          {block.items.map((it, i) => (
            <li key={`${it.title}-${i}`} className="pb-chamfer pb-light pb-problem">
              <div className="pb-problem__art" aria-hidden="true"><ItemIcon name={it.icon ?? pick(PROBLEM_ICONS, i)} size={88} /></div>
              <h3 className="pb-h3 pb-problem__title">{it.title}</h3>
              <p className="pb-body">{it.body}</p>
            </li>
          ))}
        </ul>
      </Frame>
    );
  }
  return (
    <Frame block={block}>
      <div className="pb-split">
        <div>
          <H2 id={block.id}>{block.heading}</H2>
          {block.intro ? <p className="pb-lede">{block.intro}</p> : null}
        </div>
        <ul className="pb-rows">
          {block.items.map((i) => (
            <li key={i.title}>
              <h3 className="pb-h3">{i.title}</h3>
              <p className="pb-body">{i.body}</p>
            </li>
          ))}
        </ul>
      </div>
    </Frame>
  );
}

/** Gantt-style process (decision 0004): a descending stair of gradient bars; each bar is a disclosure revealing the step. */
function Gantt({ id, steps, locale }: { id: string; steps: { title: string; body: string; duration?: string }[]; locale?: string }) {
  const t = blockMessages(locale);
  return (
    <ol className="pb-gantt" style={{ ['--n' as string]: steps.length }}>
      {steps.map((s, i) => (
        <li key={`${s.title}-${i}`} className="pb-gantt__col" style={{ ['--i' as string]: i }}>
          <span className="pb-gantt__step" aria-hidden="true">{t.step} #{i + 1}</span>
          <details className="pb-gantt__item" id={`${id}-step-${i + 1}`}>
            <summary>
              <span className="pb-gantt__title"><span className="pb-visually-hidden">{t.step} {i + 1}: </span>{s.title}</span>
              <span className={`pb-gantt__bar pb-gantt__bar--${(i % 6) + 1}`}><span className="pb-gantt__plus"><LineIcon name="plus" /></span></span>
            </summary>
            <div className="pb-gantt__body">
              {s.duration ? <p className="pb-gantt__dur">{s.duration}</p> : null}
              <p className="pb-body">{s.body}</p>
            </div>
          </details>
        </li>
      ))}
    </ol>
  );
}

export function ProcessBlock({ block, locale }: { block: BlockOf<'process'> & { inRun?: boolean }; locale?: string }) {
  if (variantOf('process', block.variant) === 'gantt') {
    return <Frame block={block} theme="strong"><SectionHead id={block.id} heading={block.heading} center /><Gantt id={block.id} steps={block.steps} locale={locale} /></Frame>;
  }
  return (
    <Frame block={block} theme="strong">
      <SectionHead id={block.id} heading={block.heading} />
      <ol className="pb-steps">
        {block.steps.map((s, i) => (
          <li key={s.title} className="pb-step">
            <span className="pb-step__n" aria-hidden="true">{i + 1}</span>
            <h3 className="pb-h3">{s.title}</h3>
            <p className="pb-body">{s.body}</p>
          </li>
        ))}
      </ol>
    </Frame>
  );
}

function TimelineBlock({ block, locale }: { block: BlockOf<'timeline'> & { inRun?: boolean }; locale?: string }) {
  if (variantOf('timeline', block.variant) === 'gantt') {
    return (
      <Frame block={block}>
        <SectionHead id={block.id} heading={block.heading} intro={block.intro} center />
        <Gantt id={block.id} steps={block.items.map((i) => ({ title: i.phase, body: i.body, duration: i.duration }))} locale={locale} />
      </Frame>
    );
  }
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} intro={block.intro} />
      <ol className="pb-timeline">
        {block.items.map((i) => (
          <li key={i.phase}>
            <h3 className="pb-h3">{i.phase}</h3>
            <p className="pb-timeline__d">{i.duration}</p>
            <p className="pb-body">{i.body}</p>
          </li>
        ))}
      </ol>
    </Frame>
  );
}

export function FaqBlock({ block, ctx }: { block: BlockOf<'faq'> & { inRun?: boolean }; ctx?: RenderContext }) {
  const t = blockMessages(ctx?.locale);
  const accordion = (
    <div className="pb-faq">
      {block.items.map((it) => (
        <details key={it.question} className="pb-faq__item">
          <summary><span>{it.question}</span><span className="pb-faq__toggle" aria-hidden="true"><LineIcon name="plus" /></span></summary>
          <p className="pb-body">{it.answer}</p>
        </details>
      ))}
    </div>
  );
  if (variantOf('faq', block.variant) === 'with-contact') {
    const b = ctx?.brand;
    return (
      <Frame block={block}>
        <div className="pb-faqc">
          <div className="pb-faqc__aside">
            <H2 id={block.id}>{block.heading}</H2>
            <div className="pb-contactcard pb-dark">
              <p className="pb-contactcard__text">{t.questions}</p>
              {b?.email ? <p className="pb-contactcard__line"><a href={`mailto:${b.email}`}>{b.email}</a></p> : null}
              {b?.phone ? <p className="pb-contactcard__line"><a href={`tel:${b.phone.replace(/[^+\d]/g, '')}`}>{b.phone}</a></p> : null}
              <Pill href="#inquiry" label={t.bookCall} variant="warm" icon="mail" />
            </div>
          </div>
          {accordion}
        </div>
      </Frame>
    );
  }
  return (
    <Frame block={block} narrow>
      <SectionHead id={block.id} heading={block.heading} center />
      {accordion}
    </Frame>
  );
}

// ---------------------------------------------------------------- convert
export function FormSection({ block, children }: { block: BlockOf<'form'> & { inRun?: boolean }; children: ReactNode }) {
  return (
    <section id="inquiry" className={`pb-section pb-inquiry pb-v-${block.visibility ?? 'all'}`} aria-labelledby={`${block.id}-h`} data-analytics-id={block.analyticsId} data-block-id={block.id}>
      <div className="pb-inquiry__band">
        <div className="pb-wrap">
          <div className="pb-inquiry__card pb-chamfer pb-light">
            <div className="pb-inquiry__head">
              <h2 id={`${block.id}-h`} className="pb-h2 pb-center">{block.heading}</h2>
              {block.intro ? <p className="pb-lede pb-center">{block.intro}</p> : null}
            </div>
            <div className="pb-inquiry__form">{children}</div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaBlock({ block }: { block: BlockOf<'cta'> & { inRun?: boolean } }) {
  const href = block.button.target === 'form' ? '#inquiry' : safeHref(block.button.href ?? '#');
  const variant = variantOf('cta', block.variant);
  if (variant === 'arc') {
    return (
      <Frame block={block} className="pb-arc">
        <div className="pb-arc__inner">
          <HeroObject small />
          <H2 id={block.id} center>{block.heading}</H2>
          {block.body ? <p className="pb-lede pb-center">{block.body}</p> : null}
          <div className="pb-actions pb-actions--center"><Pill href={href} label={block.button.label} variant="primary" /></div>
        </div>
      </Frame>
    );
  }
  if (variant === 'offer') {
    return (
      <Frame block={block}>
        <div className="pb-offer pb-light">
          <div className="pb-offer__copy">
            <H2 id={block.id} size="md">{block.heading}</H2>
            {block.body ? <p className="pb-lede">{block.body}</p> : null}
            <div className="pb-actions"><Pill href={href} label={block.button.label} variant="primary" icon="pen" /></div>
          </div>
          <div className="pb-offer__media" aria-hidden="true"><Icon3d name="diamond" size={360} /></div>
        </div>
      </Frame>
    );
  }
  return (
    <Frame block={block} theme="strong" className="pb-cta">
      <div className="pb-cta__inner">
        <div>
          <H2 id={block.id} size="md">{block.heading}</H2>
          {block.body ? <p className="pb-lede">{block.body}</p> : null}
        </div>
        <Pill href={href} label={block.button.label} variant="primary" />
      </div>
    </Frame>
  );
}

function PricingBlock({ block }: { block: BlockOf<'pricing'> & { inRun?: boolean } }) {
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} intro={block.intro} />
      <ul className="pb-plans">
        {block.plans.map((p, i) => (
          <li key={p.name} className={`pb-plan pb-light${i === 1 ? ' is-highlight' : ''}`}>
            <h3 className="pb-h3">{p.name}</h3>
            <p className="pb-plan__price">{p.priceLabel}</p>
            <p className="pb-body">{p.summary}</p>
            {p.features.length ? <ul className="pb-checks">{p.features.map((f) => <li key={f}>{f}</li>)}</ul> : null}
          </li>
        ))}
      </ul>
    </Frame>
  );
}

function ContactBlock({ block, ctx }: { block: BlockOf<'contact'> & { inRun?: boolean }; ctx: RenderContext }) {
  const t = blockMessages(ctx.locale);
  const b = block.useBrandDetails ? ctx.brand : undefined;
  return (
    <Frame block={block}>
      <div className="pb-split">
        <div>
          <H2 id={block.id}>{block.heading}</H2>
          {block.body ? <p className="pb-lede">{block.body}</p> : null}
        </div>
        <div className="pb-contactcard pb-dark">
          {b && (b.email || b.phone || b.address) ? (
            <dl className="pb-contact">
              {b.email ? <div><dt>{t.contact}</dt><dd><a href={`mailto:${b.email}`}>{b.email}</a></dd></div> : null}
              {b.phone ? <div><dt>{t.phone}</dt><dd><a href={`tel:${b.phone.replace(/[^+\d]/g, '')}`}>{b.phone}</a></dd></div> : null}
              {b.address ? <div><dt>{t.address}</dt><dd>{b.address}</dd></div> : null}
            </dl>
          ) : <p className="pb-body">{block.useBrandDetails ? t.contactMissing : t.tellProject}</p>}
          <Pill href="#inquiry" label={t.describeProject} variant="warm" icon="mail" />
        </div>
      </div>
    </Frame>
  );
}

function StickyCtaBlock({ block }: { block: BlockOf<'stickyCta'> }) {
  const href = block.target === 'form' ? '#inquiry' : safeHref(block.href ?? '#');
  return (
    <div className="pb-sticky" data-analytics-id={block.analyticsId} data-block-id={block.id}>
      <Pill href={href} label={block.label} variant="primary" />
    </div>
  );
}

// ---------------------------------------------------------------- proof (verified only)
function LogoCloudBlock({ block, ctx }: { block: BlockOf<'logoCloud'> & { inRun?: boolean }; ctx: RenderContext }) {
  const t = blockMessages(ctx.locale);
  const logos = visible(block.logos, ctx);
  if (!logos.length) return null;
  const item = (l: (typeof logos)[number], hidden?: boolean) => (
    <li key={`${l.name}${hidden ? '-dup' : ''}`} aria-hidden={hidden || undefined}>
      {l.imageUrl ? <img src={safeHref(l.imageUrl)} alt={hidden ? '' : l.name} loading="lazy" /> : <span className="pb-wordmark">{l.name}</span>}
      {!l.verified && !hidden ? <DraftTag /> : null}
    </li>
  );
  const marquee = variantOf('logoCloud', block.variant) === 'marquee';
  return (
    <Frame block={block} className="pb-tight" labelled={!!block.heading}>
      {block.heading ? <Eyebrow serif id={`${block.id}-h`}>{block.heading}</Eyebrow> : null}
      {marquee ? (
        <div className="pb-marquee">
          <label className="pb-marquee__pause"><input type="checkbox" /><span>{t.pauseAnimation}</span></label>
          <div className="pb-marquee__viewport">
            <ul className="pb-marquee__track">{logos.map((l) => item(l))}{logos.map((l) => item(l, true))}</ul>
          </div>
        </div>
      ) : <ul className="pb-logos">{logos.map((l) => item(l))}</ul>}
    </Frame>
  );
}

function TrustBarBlock({ block, locale }: { block: BlockOf<'trustBar'> & { inRun?: boolean }; locale?: string }) {
  return (
    <Frame block={block} className="pb-tight" labelled={false}>
      <ul className="pb-trust" aria-label={blockMessages(locale).howWeWork}>
        {block.items.map((i) => (
          <li key={i.label}>
            <strong>{i.label}</strong>
            {i.detail ? <span>{i.detail}</span> : null}
          </li>
        ))}
      </ul>
    </Frame>
  );
}

function MetricsBlock({ block, ctx }: { block: BlockOf<'metrics'> & { inRun?: boolean }; ctx: RenderContext }) {
  const items = visible(block.items, ctx);
  if (!items.length) return null;
  const cards = variantOf('metrics', block.variant) === 'cards';
  return (
    <Frame block={block} labelled={!!block.heading}>
      {block.heading ? <SectionHead id={block.id} heading={block.heading} center={!cards} /> : null}
      <dl className={cards ? 'pb-statcards' : 'pb-statrow'}>
        {items.map((m) => (
          <div key={m.label} className={cards ? 'pb-statcard pb-light' : 'pb-stat'}>
            <dt>{m.label}</dt>
            <dd>{m.value}{!m.verified ? <DraftTag /> : null}</dd>
          </div>
        ))}
      </dl>
    </Frame>
  );
}

function CaseStudiesBlock({ block, ctx }: { block: BlockOf<'caseStudies'> & { inRun?: boolean }; ctx: RenderContext }) {
  const action = caseStudyAction(ctx.locale);
  const all = ctx.caseStudies ?? [];
  const picked = block.slugs.length ? block.slugs.map((s) => all.find((c) => c.slug === s)).filter((c): c is NonNullable<typeof c> => !!c) : all.slice(0, 3);
  if (!picked.length) {
    // Never invent examples. The public page omits the section; the editor explains why.
    return ctx.showDraftClaims ? <Frame block={block}><H2 id={block.id}>{block.heading}</H2><p className="pb-body"><DraftTag /> No published case studies yet. This section is hidden publicly until some exist.</p></Frame> : null;
  }
  if (variantOf('caseStudies', block.variant) === 'sticky-stack') {
    return (
      <Frame block={block} theme="strong" className="pb-stack-section" wrap={false}>
        <div className="pb-wrap"><H2 id={block.id}>{block.heading}</H2></div>
        <ol className="pb-stackcases">
          {picked.map((c, i) => (
            <li key={c.slug} className="pb-stackcase pb-dark" style={c.coverImageUrl ? { backgroundImage: `url(${safeHref(c.coverImageUrl)}?w=1600)` } : undefined}>
              <div className="pb-wrap pb-stackcase__inner">
                <div className="pb-stackcase__copy">
                  <h3 className="pb-stackcase__title"><a href={safeHref(c.href)}>{c.title}</a></h3>
                  {c.industry ? <p className="pb-tags"><span>{c.industry}</span></p> : null}
                  <p className="pb-body">{c.summary}</p>
                </div>
                <div className="pb-stackcase__card pb-light">
                  <span className="pb-stackcase__count" aria-hidden="true">{String(i + 1).padStart(2, '0')} / {String(picked.length).padStart(2, '0')}</span>
                  <Pill href={c.href} label={action} variant="primary" aria-label={`${action}: ${c.title}`} />
                </div>
              </div>
            </li>
          ))}
        </ol>
      </Frame>
    );
  }
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} center />
      <ul className="pb-panels">
        {picked.map((c) => (
          <li key={c.slug} className="pb-panel pb-light">
            <div className="pb-panel__inner">
              <div className="pb-panel__copy">
                {c.industry ? <p className="pb-panel__cat">{c.industry}</p> : null}
                <h3 className="pb-panel__title"><a href={safeHref(c.href)}>{c.title}</a></h3>
                <p className="pb-body">{c.summary}</p>
              </div>
              <div className="pb-panel__media" aria-hidden="true">
                {c.coverImageUrl ? <img src={`${safeHref(c.coverImageUrl)}?w=960`} alt="" loading="lazy" /> : <Icon3d name="diamond" size={280} />}
              </div>
              <CircleArrow size={64} className="pb-panel__go" />
            </div>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

function TestimonialsBlock({ block, ctx }: { block: BlockOf<'testimonials'> & { inRun?: boolean }; ctx: RenderContext }) {
  const t = blockMessages(ctx.locale);
  const items = visible(block.items, ctx);
  if (!items.length) return null;
  const panel = variantOf('testimonials', block.variant) === 'panel';
  const by = (q: (typeof items)[number]) => <>{q.author}{q.role ? `, ${q.role}` : ''}{q.company ? `, ${q.company}` : ''}{!q.verified ? <DraftTag /> : null}</>;
  if (panel) {
    return (
      <Frame block={block} labelled={!!block.heading}>
        <div className="pb-tpanel pb-light">
          {block.heading ? <h2 id={`${block.id}-h`} className="pb-serif-xl">{block.heading}</h2> : null}
          <div className="pb-tpanel__list" role="region" aria-label={t.clientQuotes} tabIndex={0}>
            {items.map((q) => (
              <figure key={q.quote} className="pb-tpanel__quote">
                <blockquote>{q.quote}</blockquote>
                <figcaption>{by(q)}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </Frame>
    );
  }
  return (
    <Frame block={block} labelled={!!block.heading}>
      {block.heading ? <SectionHead id={block.id} heading={block.heading} /> : null}
      <div className="pb-cards3">
        {items.map((q) => (
          <figure key={q.quote} className="pb-chamfer pb-light pb-review">
            <blockquote>“{q.quote}”</blockquote>
            <figcaption>{by(q)}</figcaption>
          </figure>
        ))}
      </div>
    </Frame>
  );
}

function TeamBlock({ block, ctx }: { block: BlockOf<'team'> & { inRun?: boolean }; ctx: RenderContext }) {
  const members = visible(block.members, ctx);
  if (!members.length) return null;
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} />
      <ul className="pb-hgrid">
        {members.map((m) => (
          <li key={m.name} className="pb-hgrid__cell">
            <h3 className="pb-h3">{m.name}</h3>
            <p className="pb-eyebrow pb-eyebrow--plain">{m.role}</p>
            {m.bio ? <p className="pb-body">{m.bio}</p> : null}
            {!m.verified ? <DraftTag /> : null}
          </li>
        ))}
      </ul>
    </Frame>
  );
}

function SecurityBlock({ block, ctx }: { block: BlockOf<'security'> & { inRun?: boolean }; ctx: RenderContext }) {
  // Practices are descriptions of work; certifications are claims and need verification.
  const items = block.items.filter((i) => i.kind === 'practice' || ctx.showDraftClaims || isPublicItem(i));
  if (!items.length) return null;
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} />
      <ul className="pb-hgrid">
        {items.map((i, k) => (
          <li key={i.title} className="pb-hgrid__cell">
            <ItemIcon name={k === 0 ? 'shield' : pick(BADGES, k)} />
            <h3 className="pb-h3">{i.title}</h3>
            <p className="pb-body">{i.body}</p>
            {i.kind === 'certification' && !i.verified ? <DraftTag /> : null}
          </li>
        ))}
      </ul>
    </Frame>
  );
}

// ---------------------------------------------------------------- technical
function TechStackBlock({ block }: { block: BlockOf<'techStack'> & { inRun?: boolean } }) {
  return (
    <Frame block={block}>
      <H2 id={block.id} center className="pb-h2--caps"><span className="pb-grad pb-grad--tech">{block.heading}</span></H2>
      <dl className="pb-stackrows">
        {block.groups.map((g) => (
          <div key={g.name}>
            <dt>{g.name}</dt>
            <dd><ul className="pb-chips">{g.items.map((i) => <li key={i}><span className="pb-chip__mark" aria-hidden="true">{i.replace(/[^\p{L}\p{N}]/gu, '').slice(0, 1).toUpperCase()}</span>{i}</li>)}</ul></dd>
          </div>
        ))}
      </dl>
    </Frame>
  );
}

function IntegrationsBlock({ block }: { block: BlockOf<'integrations'> & { inRun?: boolean } }) {
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} intro={block.intro} />
      <ul className="pb-integrations">
        {block.items.map((i) => (
          <li key={i.name}>
            <span className="pb-chip__mark" aria-hidden="true">{i.name.slice(0, 1).toUpperCase()}</span>
            <span><strong>{i.name}</strong>{i.body ? <span>{i.body}</span> : null}</span>
          </li>
        ))}
      </ul>
    </Frame>
  );
}

function ArchitectureBlock({ block }: { block: BlockOf<'architecture'> & { inRun?: boolean } }) {
  return (
    <Frame block={block}>
      <div className="pb-split">
        <div>
          <H2 id={block.id}>{block.heading}</H2>
          {block.intro ? <p className="pb-lede">{block.intro}</p> : null}
        </div>
        <ol className="pb-layers pb-dark">
          {block.layers.map((l, i) => (
            <li key={l.name}>
              <span className="pb-layers__n" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <div>
                <h3 className="pb-h3">{l.name}</h3>
                <p className="pb-body">{l.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </Frame>
  );
}

function ComparisonBlock({ block }: { block: BlockOf<'comparison'> & { inRun?: boolean } }) {
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} />
      <div className="pb-tablewrap pb-light" role="region" aria-labelledby={`${block.id}-h`} tabIndex={0}>
        <table className="pb-table">
          <thead>
            <tr><td />{block.columns.map((c) => <th key={c} scope="col">{c}</th>)}</tr>
          </thead>
          <tbody>
            {block.rows.map((r) => (
              <tr key={r.label}>
                <th scope="row">{r.label}</th>
                {block.columns.map((c, i) => <td key={c}>{r.values[i] ?? ''}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Frame>
  );
}

// ---------------------------------------------------------------- connect
function LinkRows({ items }: { items: { label: string; href: string; detail?: string }[] }) {
  return (
    <ul className="pb-linkrows">
      {items.map((it) => (
        <li key={it.href + it.label}>
          <a href={safeHref(it.href)}><span><span className="pb-linkrows__label">{it.label}</span>{it.detail ? <span className="pb-linkrows__detail">{it.detail}</span> : null}</span><LineIcon name="arrow-up-right" /></a>
        </li>
      ))}
    </ul>
  );
}

function RelatedServicesBlock({ block, ctx }: { block: BlockOf<'relatedServices'> & { inRun?: boolean }; ctx: RenderContext }) {
  const picked = block.serviceKeys.map((k) => (ctx.services ?? []).find((s) => s.key === k)).filter((s): s is NonNullable<typeof s> => !!s);
  if (!picked.length) return null;
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} />
      <LinkRows items={picked.map((s) => ({ label: s.name, href: s.href, detail: s.description }))} />
    </Frame>
  );
}

function RelatedArticlesBlock({ block, ctx }: { block: BlockOf<'relatedArticles'> & { inRun?: boolean }; ctx: RenderContext }) {
  const items = (ctx.articles ?? []).slice(0, block.count);
  if (!items.length) return null;
  return (
    <Frame block={block}>
      <SectionHead id={block.id} heading={block.heading} />
      <ul className="pb-articlecards">
        {items.map((a) => (
          <li key={a.href} className="pb-articlecard pb-light">
            <h3 className="pb-h3"><a href={safeHref(a.href)}>{a.title}</a></h3>
            {a.summary ? <p className="pb-body">{a.summary}</p> : null}
            <CircleArrow />
          </li>
        ))}
      </ul>
    </Frame>
  );
}

function StatementBlock({ block }: { block: BlockOf<'statement'> & { inRun?: boolean } }) {
  return (
    <Frame block={block} labelled={false} className="pb-statement-section">
      <div className="pb-statement">
        <div className="pb-statement__aside">
          {block.badge ? <ItemIcon name={block.badge} size={72} /> : null}
          <p className="pb-statement__caption">{block.caption}</p>
        </div>
        <p className="pb-statement__body">{block.body}</p>
      </div>
    </Frame>
  );
}

function ServiceColumnsBlock({ block }: { block: BlockOf<'serviceColumns'> & { inRun?: boolean } }) {
  return (
    <Frame block={block} className="pb-columns-section">
      <div className="pb-columns__head">
        <Icon3d name="diamond" size={300} className="pb-columns__diamond" />
        <H2 id={block.id} size="xl" center>{block.heading}</H2>
      </div>
      <div className={`pb-columns pb-columns--${block.columns.length}`}>
        {block.columns.map((c, i) => (
          <div key={`${c.title}-${i}`} className="pb-columns__col">
            <div className="pb-columns__title">
              <h3 className="pb-serif-lg">{c.href ? <a href={safeHref(c.href)}>{c.title}</a> : c.title}</h3>
              <span className="pb-columns__icons" aria-hidden="true">
                {c.href ? <CircleArrow size={40} /> : null}
                <ItemIcon name={c.icon ?? pick(COLUMN_BADGES, i)} />
              </span>
            </div>
            <LinkRows items={c.items} />
          </div>
        ))}
      </div>
    </Frame>
  );
}

// ---------------------------------------------------------------- composition
function renderBlock(b: Block & { inRun?: boolean }, ctx: RenderContext): ReactNode {
  switch (b.type) {
    case 'hero': return <HeroBlock key={b.id} block={b} locale={ctx.locale} />;
    case 'logoCloud': return <LogoCloudBlock key={b.id} block={b} ctx={ctx} />;
    case 'trustBar': return <TrustBarBlock key={b.id} block={b} locale={ctx.locale} />;
    case 'metrics': return <MetricsBlock key={b.id} block={b} ctx={ctx} />;
    case 'problem': return <ProblemBlock key={b.id} block={b} />;
    case 'benefits': return <BenefitsBlock key={b.id} block={b} locale={ctx.locale} />;
    case 'services': return <ServicesBlock key={b.id} block={b} locale={ctx.locale} />;
    case 'features': return <FeaturesBlock key={b.id} block={b} locale={ctx.locale} />;
    case 'useCases': return <FeaturesBlock key={b.id} block={b} locale={ctx.locale} />;
    case 'process': return <ProcessBlock key={b.id} block={b} locale={ctx.locale} />;
    case 'timeline': return <TimelineBlock key={b.id} block={b} locale={ctx.locale} />;
    case 'caseStudies': return <CaseStudiesBlock key={b.id} block={b} ctx={ctx} />;
    case 'testimonials': return <TestimonialsBlock key={b.id} block={b} ctx={ctx} />;
    case 'techStack': return <TechStackBlock key={b.id} block={b} />;
    case 'integrations': return <IntegrationsBlock key={b.id} block={b} />;
    case 'architecture': return <ArchitectureBlock key={b.id} block={b} />;
    case 'comparison': return <ComparisonBlock key={b.id} block={b} />;
    case 'pricing': return <PricingBlock key={b.id} block={b} />;
    case 'faq': return <FaqBlock key={b.id} block={b} ctx={ctx} />;
    case 'team': return <TeamBlock key={b.id} block={b} ctx={ctx} />;
    case 'security': return <SecurityBlock key={b.id} block={b} ctx={ctx} />;
    case 'cta': return <CtaBlock key={b.id} block={b} />;
    case 'form': return <FormSection key={b.id} block={b}>{ctx.renderForm(b)}</FormSection>;
    case 'contact': return <ContactBlock key={b.id} block={b} ctx={ctx} />;
    case 'stickyCta': return <StickyCtaBlock key={b.id} block={b} />;
    case 'relatedServices': return <RelatedServicesBlock key={b.id} block={b} ctx={ctx} />;
    case 'relatedArticles': return <RelatedArticlesBlock key={b.id} block={b} ctx={ctx} />;
    case 'statement': return <StatementBlock key={b.id} block={b} />;
    case 'serviceColumns': return <ServiceColumnsBlock key={b.id} block={b} />;
  }
}

type Segment = { blocks: Block[]; run: boolean; fade: boolean };
/**
 * Gradient runs (decision 0004, brief §7.1): a run opens at a block themed `night` and closes at the first `night-fade` block (inclusive).
 * A run that never closes stays solid night to the end of the page. Blocks inside a run drop their own background.
 */
export function segmentBlocks(blocks: Block[]): Segment[] {
  const out: Segment[] = [];
  let open: Segment | null = null;
  for (const b of blocks) {
    if (open) {
      open.blocks.push(b);
      if (b.theme === 'night-fade') { open.fade = true; open = null; }
      continue;
    }
    if (b.theme === 'night' || b.theme === 'night-fade') {
      const seg: Segment = { blocks: [b], run: true, fade: b.theme === 'night-fade' };
      out.push(seg);
      if (!seg.fade) open = seg;
      continue;
    }
    const last = out[out.length - 1];
    if (last && !last.run) last.blocks.push(b);
    else out.push({ blocks: [b], run: false, fade: false });
  }
  return out;
}

/** Renders the block list. Unknown or unverified content never produces output: hidden claims, empty connected sections. */
export function renderBlocks(blocks: Block[], ctx: RenderContext) {
  const hero = blocks.find((b): b is BlockOf<'hero'> => b.type === 'hero');
  const style = hero?.variant === 'centered-dark' ? 'service' : hero?.variant === 'centered-visual' ? 'home' : 'default';
  let runs = 0;
  return (
    <div className={`pb-page pb-page--${style}`}>
      {segmentBlocks(blocks).map((seg, i) => {
        if (!seg.run) return seg.blocks.map((b) => renderBlock(b, ctx));
        const n = ++runs;
        return (
          <div key={`run-${i}`} className={`pb-run pb-dark ${seg.fade ? `pb-run--fade pb-run--g${n === 1 ? 1 : 2}` : 'pb-run--solid'}`}>
            {seg.blocks.map((b) => renderBlock({ ...b, inRun: true }, ctx))}
            {seg.fade ? <div className="pb-run__tail" aria-hidden="true" /> : null}
          </div>
        );
      })}
    </div>
  );
}

export { ArticleView, CaseStudyView, RichText } from './content.tsx';
export { CircleArrow, Eyebrow, HeroObject, Icon3d, ItemIcon, LineIcon, Pill, PillLabel, safeHref, type PillVariant, type UiIcon } from './ui.tsx';
